/**
 * Terms of Service Content — shared between TOS gate and public /terms page.
 * Version and effective date are exported for use in acceptance recording.
 * Includes lawyer-verified disclaimer language.
 */

import React from 'react';

export const TOS_VERSION = '1.1';
export const TOS_EFFECTIVE_DATE = '31 March 2026';

export default function TermsContent() {
  return (
    <>
      <p className="text-xs text-gray-400">Version {TOS_VERSION} &mdash; Effective {TOS_EFFECTIVE_DATE}</p>

      <h2 className="text-base font-semibold text-gray-900">1. Acceptance</h2>
      <p>
        By accessing or using Heyward (&ldquo;the Platform&rdquo;), you agree to be bound by these
        Terms of Service (&ldquo;Terms&rdquo;). If you do not agree, you must not access or use the Platform.
        These Terms constitute a legally binding agreement between you (&ldquo;User&rdquo;) and
        MindFrontier Pty Ltd ABN 14 685 709 433 (&ldquo;MindFrontier&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;).
      </p>

      <h2 className="text-base font-semibold text-gray-900">2. Licence Grant</h2>
      <p>
        Subject to these Terms, we grant you a limited, non-exclusive, non-transferable, revocable licence
        to access and use the Platform solely for your own lawful property investment evaluation purposes.
        This licence does not include any right to sublicence, redistribute, or make the Platform available
        to third parties.
      </p>

      <h2 className="text-base font-semibold text-gray-900">3. Intellectual Property &amp; Proprietary Rights</h2>
      <p>
        All intellectual property in the Platform &mdash; including but not limited to source code, algorithms,
        data models, AI prompts, workflows, user interface designs, visual design elements, layout patterns,
        interaction patterns, colour schemes, typography choices, component architecture, risk assessment
        methodologies, gate systems, deal copilot logic, portfolio intelligence engines, strategy calculators,
        suburb scanning systems, and all documentation &mdash; is and remains the exclusive property of MindFrontier.
      </p>
      <p>
        &ldquo;Intellectual Property&rdquo; includes all patents, copyrights, trade secrets, trademarks,
        design rights, database rights, and all other intellectual property rights, whether registered
        or unregistered, and including all applications and rights to apply for them.
      </p>
      <p>
        Unauthorised reproduction or sharing of any copyrighted materials from the Platform will result
        in immediate access revocation, and MindFrontier reserves the right to pursue all available
        legal remedies to address such breaches.
      </p>

      {/* Section 4 — the critical anti-copying clause, visually highlighted */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-4">
        <h2 className="text-base font-bold text-red-900 mb-2">
          4. PROHIBITION ON COPYING, DUPLICATION, REVERSE ENGINEERING &amp; COMPETITIVE USE
        </h2>
        <p className="font-semibold text-red-800 text-sm mb-3">
          THIS SECTION CONTAINS CRITICAL RESTRICTIONS. VIOLATION WILL RESULT IN IMMEDIATE LEGAL ACTION,
          INCLUDING INJUNCTIVE RELIEF AND RECOVERY OF ALL LEGAL COSTS FROM THE VIOLATING PARTY.
        </p>
        <p className="font-medium">
          You expressly agree that you will NOT, directly or indirectly:
        </p>
        <ul className="list-disc pl-6 space-y-2 mt-2">
          <li>
            <strong>Copy, replicate, reproduce, duplicate, or imitate</strong> the Platform or any part
            thereof, including but not limited to its features, functionality, user interface designs,
            visual design elements, layout patterns, page structures, component designs, interaction
            flows, data visualisations, workflows, data pipelines, risk assessment methods, gate-based
            action systems, deal tracking logic, portfolio dashboards, strategy calculators, cash flow
            projections, scenario modelling, or AI-assisted analysis capabilities;
          </li>
          <li>
            <strong>Reverse engineer, decompile, disassemble, or otherwise attempt to derive</strong> the
            source code, algorithms, prompts, data models, or any underlying technology of the Platform,
            whether through manual inspection, automated tools, or AI-assisted analysis;
          </li>
          <li>
            <strong>Build, develop, commission, or assist any person or entity in building</strong> any
            product, service, application, website, or tool that is substantially similar to, or
            competitive with, the Platform or any of its features &mdash; whether using the same or
            different technology, including but not limited to AI models such as Gemini, ChatGPT, Claude,
            Copilot, or any other large language model, code generation tool, or design tool;
          </li>
          <li>
            <strong>Copy, replicate, or imitate the user experience (UX), user interface (UI), visual
            design, or design methodology</strong> of the Platform, including its layout patterns,
            navigation structures, information architecture, card designs, dashboard layouts, data
            presentation formats, colour palettes, typography systems, or any other design element
            that forms part of the Platform&apos;s distinctive look and feel;
          </li>
          <li>
            <strong>Share, disclose, or make available</strong> any proprietary information, methodologies,
            screenshots, screen recordings, workflows, output formats, design patterns, or any other
            aspect of the Platform to any third party for the purpose of enabling the creation of a
            competing or similar product or service;
          </li>
          <li>
            <strong>Use knowledge, insights, or concepts gained from the Platform</strong> &mdash; including
            its approach to property due diligence, risk analysis, deal management, portfolio intelligence,
            suburb scanning, investment strategy modelling, or any other methodology &mdash; as a basis
            for developing competing tools, services, or products;
          </li>
          <li>
            <strong>Scrape, crawl, or systematically extract</strong> data, reports, analyses, design
            assets, or other content generated by or contained within the Platform for use outside the
            Platform, for training AI models, or for any commercial purpose;
          </li>
          <li>
            <strong>Engage any third party</strong> (including developers, designers, consultants, or
            AI tools) to perform any of the above acts on your behalf or using information you have
            obtained from the Platform.
          </li>
        </ul>

        <div className="mt-4 pt-3 border-t border-red-200">
          <p className="text-sm font-bold text-red-900">Consequences of Breach:</p>
          <p className="text-sm text-red-800 mt-1">
            Any breach of this Section 4 will be treated as a material breach of these Terms and
            will result in:
          </p>
          <ul className="list-disc pl-6 space-y-1 mt-2 text-sm text-red-800">
            <li>
              <strong>Immediate and permanent termination</strong> of your access to the Platform
              without refund;
            </li>
            <li>
              <strong>Pursuit of injunctive relief</strong> (including interim, interlocutory, and
              permanent injunctions) in any court of competent jurisdiction to prevent further
              infringement;
            </li>
            <li>
              <strong>Recovery of all damages</strong>, including but not limited to actual damages,
              lost profits, statutory damages under the Copyright Act 1968 (Cth) and related
              legislation, and any gains or profits attributable to the infringement;
            </li>
            <li>
              <strong>Recovery of all legal costs</strong> &mdash; you agree that in the event of any
              legal proceedings arising from a breach of this Section, you will be liable for
              MindFrontier&apos;s legal costs on a full indemnity (solicitor-client) basis, including
              all fees, disbursements, expert costs, and related expenses;
            </li>
            <li>
              <strong>Referral to relevant authorities</strong> where applicable, including for
              criminal prosecution under the Copyright Act 1968 (Cth) where wilful infringement
              is established.
            </li>
          </ul>
        </div>
      </div>

      <h2 className="text-base font-semibold text-gray-900">5. Confidentiality</h2>
      <p>
        You acknowledge that through your use of the Platform you may be exposed to confidential and
        proprietary information, including the Platform&apos;s features, capabilities, limitations,
        roadmap, pricing, technical architecture, design system, and methodologies (&ldquo;Confidential
        Information&rdquo;). You agree to hold all Confidential Information in strict confidence and
        not to disclose it to any third party without MindFrontier&apos;s prior written consent.
      </p>
      <p>
        This obligation of confidentiality survives termination of your account and continues
        indefinitely.
      </p>

      <h2 className="text-base font-semibold text-gray-900">6. Permitted Use</h2>
      <p>
        You may use the Platform solely for evaluating properties for your own investment decisions or
        for providing advice to your direct clients in your capacity as a licensed professional (e.g.,
        buyer&apos;s advocate, financial advisor). You must not resell, white-label, or redistribute
        access to the Platform or its outputs.
      </p>

      <h2 className="text-base font-semibold text-gray-900">7. Account Security</h2>
      <p>
        Your account is personal to you. You must not share your login credentials or allow any other
        person to access the Platform through your account. You are responsible for all activity that
        occurs under your account. You must notify us immediately of any unauthorised access.
      </p>

      <h2 className="text-base font-semibold text-gray-900">8. Data &amp; Privacy</h2>
      <p>
        We collect and process personal data in accordance with the Australian Privacy Principles under
        the Privacy Act 1988 (Cth). For full details, please see our{' '}
        <a href="/privacy" className="text-blue-600 underline hover:text-blue-800">Privacy Policy</a>.
      </p>

      <h2 className="text-base font-semibold text-gray-900">9. Disclaimer &amp; No Professional Advice</h2>
      <p>
        The material and content provided on the Platform is for informational purposes only and general
        in nature and must not be used or relied upon as a substitute for obtaining independent legal,
        investment, financial, or other professional advice from appropriately qualified professional
        persons based on your particular circumstances.
      </p>
      <p>
        <strong>The Platform does not constitute financial advice, legal advice, tax advice, or a
        recommendation to buy, sell, or hold any property or financial product.</strong> You are
        solely responsible for your investment decisions. All information, analyses, projections,
        and examples provided by the Platform relate to general market data and AI-generated
        assessments which are not a reliable indicator of future performance.
      </p>
      <p>
        Property market information is based on data and sources believed to be reliable but may not
        reflect the current state of the market. Users should perform their own due diligence before
        making any property-related decisions.
      </p>
      <p>
        Investing in property and financial products carries a risk of loss. Past performance is not
        indicative of future results. Investment values can fluctuate, and investors may not get back
        the amount originally invested. Users should carefully consider their investment objectives,
        experience, and risk appetite before making investment decisions.
      </p>
      <p>
        Interest rates and financial product information displayed on the Platform are subject to change
        without notice and may not apply in all circumstances. Users should verify current rates and
        terms directly with the financial institutions offering the products. We do not endorse or
        recommend any specific financial products or institutions.
      </p>

      <h2 className="text-base font-semibold text-gray-900">10. Limitation of Liability</h2>
      <p>
        To the maximum extent permitted by law, MindFrontier, its directors, officers, employees,
        agents, contractors, and related parties shall not be liable for any indirect, incidental,
        special, consequential, or punitive damages, or any loss of profits, revenues, data, use,
        goodwill, or other intangible losses resulting from:
      </p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Your use of, or inability to use, the Platform;</li>
        <li>Any reliance placed on information provided by the Platform;</li>
        <li>Any property investment, purchase, or financial decision made based on Platform outputs;</li>
        <li>Any errors, omissions, or inaccuracies in the data, analyses, or content;</li>
        <li>Any unauthorised access to or alteration of your data;</li>
        <li>Any third-party conduct or content on the Platform.</li>
      </ul>
      <p className="mt-2">
        MindFrontier does not make any representations or warranties of any kind, express or implied,
        about the completeness, accuracy, reliability, suitability, or availability of the Platform or
        the information, products, services, or related graphics contained on the Platform for any
        purpose. Any reliance you place on such information is therefore strictly at your own risk.
        Users are advised to independently verify any information before relying on it.
      </p>

      <h2 className="text-base font-semibold text-gray-900">11. Third-Party Links &amp; Services</h2>
      <p>
        The Platform may include links to other websites and/or resources provided by third parties.
        These links are provided for your convenience only. We have no control over the content of
        those sites or resources and accept no responsibility for them or for any loss or damage that
        may arise from your use of them. The inclusion of any links does not necessarily imply a
        recommendation or endorse the views expressed within them.
      </p>

      <h2 className="text-base font-semibold text-gray-900">12. Indemnification</h2>
      <p>
        You agree to indemnify, defend, and hold harmless MindFrontier, its directors, officers,
        employees, agents, and related parties from and against any and all claims, damages, losses,
        liabilities, costs, and expenses (including reasonable legal fees on a full indemnity basis)
        arising from or related to:
      </p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Your use or misuse of the Platform;</li>
        <li>Your breach of these Terms, including but not limited to Sections 3, 4, and 5;</li>
        <li>Any investment or property decision you make based on Platform outputs;</li>
        <li>Your violation of any applicable law or the rights of any third party.</li>
      </ul>

      <h2 className="text-base font-semibold text-gray-900">13. Termination</h2>
      <p>
        We may suspend or terminate your access to the Platform at any time, with or without cause,
        and with or without notice. Upon termination, all licences and rights granted to you under
        these Terms will immediately cease. Sections 3, 4, 5, 9, 10, 12, and 14 survive termination.
      </p>

      <h2 className="text-base font-semibold text-gray-900">14. Remedies &amp; Enforcement</h2>
      <p>
        You acknowledge that any breach of Sections 3, 4, or 5 would cause irreparable harm to
        MindFrontier for which monetary damages would be inadequate. MindFrontier shall be entitled
        to seek injunctive relief (including interim, interlocutory, and permanent injunctions) in
        any court of competent jurisdiction, in addition to any other remedies available at law or
        in equity, without the need to prove actual damages or post a bond.
      </p>
      <p>
        In any legal proceedings arising from a breach of these Terms, the breaching party agrees
        to pay MindFrontier&apos;s legal costs on a full indemnity (solicitor-client) basis.
      </p>

      <h2 className="text-base font-semibold text-gray-900">15. Governing Law &amp; Jurisdiction</h2>
      <p>
        These Terms are governed by the laws of Victoria, Australia. You submit to the exclusive
        jurisdiction of the courts of Victoria for any disputes arising from or relating to these Terms.
      </p>

      <h2 className="text-base font-semibold text-gray-900">16. Amendments</h2>
      <p>
        We reserve the right to modify these Terms at any time without prior notice. When we do, we
        will update the version number and effective date. Your continued use of the Platform after
        changes are posted constitutes acceptance of the updated Terms. If you do not agree with any
        part of these Terms, you must cease using the Platform immediately.
      </p>

      <div className="border-t border-gray-200 pt-4 mt-6">
        <p className="text-xs text-gray-400">
          Heyward is a product of MindFrontier Pty Ltd. All rights reserved. Every effort has been made to
          ensure the accuracy of the information contained on this Platform. However, as the property market is
          continually evolving, no guarantees are given that the information contained within this Platform is
          currently correct. MindFrontier shall bear no liability to any person or entity with respect to any
          inaccuracy, misleading information, loss or damage caused directly or indirectly by the information
          contained on the Platform.
        </p>
      </div>
    </>
  );
}
